/**
 * Shuttle Thank You Page Manager
 * Handles dynamic content population on the thank you page
 * 
 * @package A2A_Theme
 */

class ShuttleThankYouManager {
    constructor() {
        this.bookingData = null;
        this.orderData = null;
        this.reference = null;
        this.userEmail = null;
    }
    
    /**
     * Initialize thank you page
     */
    init() {
        // Get reference from URL (always present on thank you page)
        this.reference = this.getReferenceFromURL();
        
        if (!this.reference) {
            return;
        }
        
        // Try to load from reference-based storage first (for returning users)
        const refData = this.loadFromReferenceStorage(this.reference);
        
        if (refData) {
            // User is returning to an old booking
            this.bookingData = refData.bookingData;
            this.orderData = refData.orderData;
            this.userEmail = refData.userEmail;
        } else {
            // New completed booking - load from session storage
            this.bookingData = this.getBookingData();
            this.orderData = this.getOrderData();
            this.userEmail = this.getUserEmail();
            
            // Save to reference storage for future access
            this.saveToReferenceStorage(this.reference);
            
            // Mark session as completed (for cleanup on next booking)
            this.markBookingAsCompleted();
        }
        
        if (!this.bookingData) {
            return;
        }
        
        // Clean up expired reference data
        this.cleanupExpiredReferences();
        
        // Update page content
        this.updateReference();
        this.updateUserEmail();
        this.updateVoucherLink();
        this.updateBookingSummary();
    }
    
    /**
     * Save booking data to reference-based storage
     * Allows users to revisit thank you page for up to 30 days
     */
    saveToReferenceStorage(reference) {
        try {
            const refKey = `shuttle_booking_ref_${reference}`;
            const refData = {
                reference: reference,
                timestamp: Date.now(),
                expiresAt: Date.now() + (30 * 24 * 60 * 60 * 1000), // 30 days
                bookingData: this.bookingData,
                orderData: this.orderData,
                userEmail: this.userEmail
            };
            
            localStorage.setItem(refKey, JSON.stringify(refData));
        } catch (e) {
            // Non-critical error - page still works with session storage
        }
    }
    
    /**
     * Load booking data from reference-based storage
     * Returns null if not found or expired
     */
    loadFromReferenceStorage(reference) {
        try {
            const refKey = `shuttle_booking_ref_${reference}`;
            const refDataStr = localStorage.getItem(refKey);
            
            if (!refDataStr) {
                return null;
            }
            
            const refData = JSON.parse(refDataStr);
            
            // Check if expired
            if (refData.expiresAt && Date.now() > refData.expiresAt) {
                localStorage.removeItem(refKey);
                return null;
            }
            
            return refData;
        } catch (e) {
            return null;
        }
    }
    
    /**
     * Clean up expired reference storage entries
     * Keeps localStorage from filling up with old bookings
     */
    cleanupExpiredReferences() {
        try {
            const now = Date.now();
            const keysToRemove = [];
            
            // Scan all localStorage keys for expired references
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                
                if (key && key.startsWith('shuttle_booking_ref_')) {
                    try {
                        const data = JSON.parse(localStorage.getItem(key));
                        
                        // Remove if expired (older than 30 days)
                        if (data.expiresAt && now > data.expiresAt) {
                            keysToRemove.push(key);
                        }
                    } catch (e) {
                        // Invalid JSON - remove it
                        keysToRemove.push(key);
                    }
                }
            }
            
                // Remove expired entries
                keysToRemove.forEach(key => {
                    localStorage.removeItem(key);
                });
            } catch (e) {
                // Error cleaning up expired references
            }
    }
    
    /**
     * Mark the current booking session as completed
     * This flag will be checked when user starts a new booking
     */
    markBookingAsCompleted() {
        try {
            const completionData = {
                completed: true,
                timestamp: Date.now(),
                reference: this.reference
            };
            localStorage.setItem('shuttle_booking_completed', JSON.stringify(completionData));
        } catch (e) {
            // Failed to mark booking as completed
        }
    }
    
    /**
     * Get reference number from URL parameter
     */
    getReferenceFromURL() {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('reference');
    }
    
    /**
     * Get booking data from localStorage
     */
    getBookingData() {
        try {
            const stored = localStorage.getItem('shuttle_booking_data');
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (e) {
            console.error('[ShuttleThankYou] Error parsing booking data:', e);
        }
        return null;
    }
    
    /**
     * Get order data from localStorage
     */
    getOrderData() {
        try {
            const stored = localStorage.getItem('shuttle_order_data');
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (e) {
            console.error('[ShuttleThankYou] Error parsing order data:', e);
        }
        return null;
    }
    
    /**
     * Get user email from localStorage checkout data
     */
    getUserEmail() {
        try {
            // Try to get from checkout data first
            const checkoutData = localStorage.getItem('shuttle_checkout_data');
            if (checkoutData) {
                const parsed = JSON.parse(checkoutData);
                if (parsed.leadPassenger && parsed.leadPassenger.email) {
                    return parsed.leadPassenger.email;
                }
            }
            
            // Fallback: try to get from booking data if it was stored there
            if (this.bookingData && this.bookingData.email) {
                return this.bookingData.email;
            }
        } catch (e) {
            console.error('[ShuttleThankYou] Error getting user email:', e);
        }
        return 'example@email.com'; // Fallback
    }
    
    /**
     * Update booking reference on page
     */
    updateReference() {
        if (this.reference) {
            jQuery('.shuttle-thank-you__booking-reference strong').text(this.reference);
        }
    }
    
    /**
     * Update user email on page
     */
    updateUserEmail() {
        if (this.userEmail) {
            jQuery('.shuttle-thank-you__subtitle strong').text(this.userEmail);
        }
    }
    
    /**
     * Update voucher download link
     */
    updateVoucherLink() {
        if (this.orderData && this.orderData.key) {
            const apiBaseUrl = typeof a2a_get_api_base_url === 'function' ? a2a_get_api_base_url() : '';
            const voucherUrl = `${apiBaseUrl}/booking/shuttle/voucher?key=${this.orderData.key}`;
            
            jQuery('.shuttle-thank-you__download-btn').on('click', function() {
                window.open(voucherUrl, '_blank');
            });
        }
    }
    
    /**
     * Update complete booking summary
     */
    updateBookingSummary() {
        // Update outbound section
        this.updateTripSection('outbound');
        
        // Update return section if applicable
        const isReturnTrip = this.bookingData.transferType === 'return';
        if (isReturnTrip) {
            this.updateTripSection('return');
            jQuery('.shuttle-thank-you__section').eq(1).show(); // Show return section
        } else {
            jQuery('.shuttle-thank-you__section').eq(1).hide(); // Hide return section
            jQuery('.shuttle-thank-you__divider').eq(1).hide(); // Hide second divider
        }
        
        // Update summary totals
        this.updateSummaryTotals();
    }
    
    /**
     * Update a trip section (outbound or return)
     */
    updateTripSection(direction) {
        const route = this.bookingData.selectedRoutes?.[direction];
        if (!route) return;
        
        const sectionIndex = direction === 'outbound' ? 0 : 1;
        const section = jQuery('.shuttle-thank-you__section').eq(sectionIndex);
        
        // Get trip data
        const isOutbound = direction === 'outbound';
        const fromLabel = isOutbound ? this.bookingData.from.label : this.bookingData.to.label;
        const toLabel = isOutbound ? this.bookingData.to.label : this.bookingData.from.label;
        const routeText = `${fromLabel} → ${toLabel}`;
        
        // Format date
        const dateStr = route.routeData?.date || '';
        const formattedDate = this.formatDate(dateStr);
        
        // Get passenger info
        const adults = this.bookingData.passengers?.adults || 0;
        const children = this.bookingData.passengers?.children || 0;
        const totalPassengers = adults + children;
        
        // Get TOTAL price for this transfer (including seats, fare, extras)
        let transferTotal = 0;
        if (this.orderData?.calculatedTotals?.[direction]) {
            // Use the calculated total from order data (includes everything)
            transferTotal = this.orderData.calculatedTotals[direction].price || 0;
        } else {
            // Fallback: calculate from booking data
            transferTotal = this.calculateTripTotal(direction);
        }
        
        const formattedPrice = this.formatPrice(transferTotal);
        
        // Get the actual departure time user selected
        const departureTime = route.departureTime || '';
        
        // Update route and date with actual departure time
        section.find('.shuttle-thank-you__trip-route p:first span:first').text(routeText);
        section.find('.shuttle-thank-you__trip-route p:last').text(`${adults} adults, ${children} kids. ${this.formatDateWithDepartureTime(dateStr, departureTime)}.`);
        
        // Update price
        section.find('.shuttle-thank-you__price-amount').text(formattedPrice);
        
        // Update extras
        this.updateTripExtras(direction, section);
    }
    
    /**
     * Derive the EUR->display-currency conversion rate used at purchase time from the
     * stored per-leg totals (price / price_eur). Using the stored figures guarantees
     * the converted line items reconcile with the charged leg totals, regardless of
     * when the page is viewed or how live exchange rates have since moved. Returns 1
     * (no conversion) when totals are unavailable (e.g. very old bookings).
     */
    getEurToDisplayRate() {
        const totals = this.orderData?.calculatedTotals;
        if (totals) {
            for (const dir of ['outbound', 'return']) {
                const t = totals[dir];
                if (t && typeof t.price === 'number' && typeof t.price_eur === 'number' && t.price_eur > 0) {
                    return t.price / t.price_eur;
                }
            }
        }
        return 1;
    }
    
    /**
     * Update extras for a trip section
     */
    updateTripExtras(direction, section) {
        const extrasContainer = section.find('.shuttle-thank-you__extras');
        extrasContainer.empty();
        
        const passengers = (this.bookingData.passengers?.adults || 0) + (this.bookingData.passengers?.children || 0);
        
        // Extras/tier/discount are computed in EUR then converted to the display
        // currency using the same rate that produced the stored leg totals.
        const rate = this.getEurToDisplayRate();
        
        // Fare condition - real per-route tier delta in EUR
        const route = this.bookingData.selectedRoutes?.[direction];
        const fareCondition = this.bookingData.pricePreferences?.tier || 'skier';
        let fareName = 'Alps2Alps Basic';
        let farePrice = 0;
        
        if (fareCondition === 'skier') {
            fareName = 'Alps2Alps Skier';
            farePrice = this.getTierDeltaEUR(route, 'skier') * passengers * rate;
        } else if (fareCondition === 'pro') {
            fareName = 'Alps2Alps Pro';
            farePrice = this.getTierDeltaEUR(route, 'pro') * passengers * rate;
        }
        
        this.addExtraItem(extrasContainer, fareName, passengers, farePrice);
        
        // Return discount (admin-configured % off transfer + tier price, return leg only)
        if (direction === 'return' && route) {
            const discountRate = this.getReturnDiscountRate();
            const seatPriceEUR = route.price?.amount || 0;
            const tierDeltaEUR = this.getTierDeltaEUR(route, fareCondition);
            const discountEUR = (seatPriceEUR + tierDeltaEUR) * passengers * discountRate;
            if (discountEUR > 0) {
                const discountPercentLabel = Number((discountRate * 100).toFixed(1));
                const discountHtml = `
                    <div class="shuttle-thank-you__extra-item">
                        <span class="shuttle-thank-you__extra-name">Return discount (${discountPercentLabel}% off)</span>
                        <div class="shuttle-thank-you__extra-pricing">
                            <span class="shuttle-thank-you__extra-quantity"></span>
                            <span class="shuttle-thank-you__extra-price">-${this.formatPrice(discountEUR * rate)}</span>
                        </div>
                    </div>
                `;
                extrasContainer.append(discountHtml);
            }
        }
        
        // Luggage
        if (this.bookingData.luggage) {
            if (this.bookingData.luggage.additional_bag > 0) {
                this.addExtraItem(
                    extrasContainer,
                    'Additional luggage',
                    this.bookingData.luggage.additional_bag,
                    20 * this.bookingData.luggage.additional_bag * rate
                );
            }
            
            if (this.bookingData.luggage.ski_bag > 0) {
                this.addExtraItem(
                    extrasContainer,
                    'Additional ski / snowboard bags',
                    this.bookingData.luggage.ski_bag,
                    20 * this.bookingData.luggage.ski_bag * rate
                );
            }
        }
        
        // Child seats
        if (this.bookingData.childSeats) {
            if (this.bookingData.childSeats.booster > 0) {
                this.addExtraItem(
                    extrasContainer,
                    'Child Booster Seat',
                    this.bookingData.childSeats.booster,
                    2 * this.bookingData.childSeats.booster * rate
                );
            }
            
            if (this.bookingData.childSeats.regular > 0) {
                this.addExtraItem(
                    extrasContainer,
                    'Child seat',
                    this.bookingData.childSeats.regular,
                    2 * this.bookingData.childSeats.regular * rate
                );
            }
        }
    }
    
    /**
     * Add an extra item to the container
     */
    addExtraItem(container, name, quantity, price) {
        const formattedPrice = this.formatPrice(price);
        const html = `
            <div class="shuttle-thank-you__extra-item">
                <span class="shuttle-thank-you__extra-name">${name}</span>
                <div class="shuttle-thank-you__extra-pricing">
                    <span class="shuttle-thank-you__extra-quantity">x${quantity}</span>
                    <span class="shuttle-thank-you__extra-price">${formattedPrice}</span>
                </div>
            </div>
        `;
        container.append(html);
    }
    
    /**
     * Update summary totals at bottom
     */
    updateSummaryTotals() {
        let outboundTotal = 0;
        let returnTotal = 0;
        
        // Calculate outbound total
        if (this.orderData?.calculatedTotals?.outbound) {
            outboundTotal = this.orderData.calculatedTotals.outbound.price || 0;
        } else if (this.bookingData.selectedRoutes?.outbound) {
            outboundTotal = this.calculateTripTotal('outbound');
        }
        
        // Calculate return total if applicable
        if (this.bookingData.transferType === 'return') {
            if (this.orderData?.calculatedTotals?.return) {
                returnTotal = this.orderData.calculatedTotals.return.price || 0;
            } else if (this.bookingData.selectedRoutes?.return) {
                returnTotal = this.calculateTripTotal('return');
            }
        }
        
        const grandTotal = outboundTotal + returnTotal;
        
        // Update display
        jQuery('.shuttle-thank-you__summary-row').eq(0).find('.shuttle-thank-you__summary-value').text(this.formatPrice(outboundTotal));
        
        if (this.bookingData.transferType === 'return') {
            jQuery('.shuttle-thank-you__summary-row').eq(1).show().find('.shuttle-thank-you__summary-value').text(this.formatPrice(returnTotal));
        } else {
            jQuery('.shuttle-thank-you__summary-row').eq(1).hide();
        }
        
        jQuery('.shuttle-thank-you__total-value').text(this.formatPrice(grandTotal));
    }
    
    /**
     * Get the real fare-tier upgrade delta (per passenger, EUR) for a route.
     * Mirrors ShuttleBookingManager.getTierDeltaEUR() (this class is standalone).
     * Falls back to the legacy hardcoded delta (+10 skier / +20 pro) if real tier
     * data is missing for the route.
     */
    getTierDeltaEUR(route, tier) {
        const hardcodedDelta = { basic: 0, skier: 10, pro: 20 };
        
        if (!tier || tier === 'basic') {
            return 0;
        }
        
        const tiers = route && route.price && route.price.tiers ? route.price.tiers : null;
        
        if (tiers && typeof tiers === 'object') {
            const tierPrice = tiers[tier];
            const basicPrice = tiers.basic;
            
            if (typeof tierPrice === 'number' && !isNaN(tierPrice) &&
                typeof basicPrice === 'number' && !isNaN(basicPrice)) {
                const delta = tierPrice - basicPrice;
                return delta > 0 ? delta : 0;
            }
        }
        
        return hardcodedDelta[tier] || 0;
    }
    
    /**
     * Get the admin-configured return-transfer discount rate as a decimal (e.g. 0.20 for 20%).
     * Mirrors ShuttleBookingManager.getReturnDiscountRate() (this class is standalone).
     * Falls back to 20% if window.shuttleReturnDiscountPercent isn't set on the page.
     */
    getReturnDiscountRate() {
        const percent = (typeof window.shuttleReturnDiscountPercent === 'number' && !isNaN(window.shuttleReturnDiscountPercent))
            ? window.shuttleReturnDiscountPercent
            : 20;
        return Math.max(0, Math.min(100, percent)) / 100;
    }
    
    /**
     * Calculate trip total (fallback if no calculated totals)
     */
    calculateTripTotal(direction) {
        const route = this.bookingData.selectedRoutes?.[direction];
        if (!route) return 0;
        
        const passengers = (this.bookingData.passengers?.adults || 0) + (this.bookingData.passengers?.children || 0);
        const seatsTotal = (route.price?.amount || 0) * passengers;
        
        // Add fare upgrade using the real per-route tier delta
        const fareCondition = this.bookingData.pricePreferences?.tier || 'basic';
        const fareUpgradeTotal = this.getTierDeltaEUR(route, fareCondition) * passengers;
        
        // Admin-configured discount applies to the return leg's transfer + tier price
        let total = seatsTotal + fareUpgradeTotal;
        if (direction === 'return') {
            total -= (seatsTotal + fareUpgradeTotal) * this.getReturnDiscountRate();
        }
        
        // Add extras
        if (this.bookingData.luggage) {
            total += (this.bookingData.luggage.additional_bag || 0) * 20;
            total += (this.bookingData.luggage.ski_bag || 0) * 20;
        }
        
        if (this.bookingData.childSeats) {
            total += (this.bookingData.childSeats.regular || 0) * 2;
            total += (this.bookingData.childSeats.booster || 0) * 2;
        }
        
        return total;
    }
    
    /**
     * Format date string
     */
    formatDate(dateString) {
        // Input: "08.10.25, 09:47"
        // Output: "Sat, 8 Mar 2025, 13:00"
        if (!dateString) return '';
        
        const parts = dateString.split(', ');
        if (parts.length !== 2) return dateString;
        
        const [datePart, timePart] = parts;
        const [day, month, year] = datePart.split('.');
        
        const fullYear = '20' + year;
        const date = new Date(fullYear, month - 1, day);
        
        const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
        const monthName = date.toLocaleDateString('en-US', { month: 'short' });
        
        return `${weekday}, ${parseInt(day)} ${monthName} ${fullYear}, ${timePart}`;
    }
    
    /**
     * Format date with the actual departure time user selected
     */
    formatDateWithDepartureTime(dateString, departureTime) {
        // Input: dateString = "08.10.25, 09:47", departureTime = "13:00"
        // Output: "Wed, 8 Oct 2025, 13:00"
        if (!dateString) return '';
        
        const parts = dateString.split(', ');
        if (parts.length !== 2) return dateString;
        
        const [datePart] = parts;
        const [day, month, year] = datePart.split('.');
        
        const fullYear = '20' + year;
        const date = new Date(fullYear, month - 1, day);
        
        const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
        const monthName = date.toLocaleDateString('en-US', { month: 'short' });
        
        // Use the actual departure time that user selected from the route
        const time = departureTime || parts[1];
        
        return `${weekday}, ${parseInt(day)} ${monthName} ${fullYear}, ${time}`;
    }
    
    /**
     * Format price with currency
     * Uses the currency from the actual payment (orderData.calculatedTotals)
     */
    formatPrice(amount) {
        // Get currency from order data (payment currency) or default to EUR
        // The payment currency is stored in orderData.calculatedTotals which reflects the actual currency used for payment
        let currency = 'EUR'; // Default
        
        if (this.orderData?.calculatedTotals?.outbound?.currency) {
            currency = this.orderData.calculatedTotals.outbound.currency;
        } else if (this.orderData?.calculatedTotals?.return?.currency) {
            currency = this.orderData.calculatedTotals.return.currency;
        } else if (this.bookingData?.currency) {
            currency = this.bookingData.currency;
        }
        
        // Format based on currency
        const rounded = Math.round(amount * 100) / 100;
        
        switch (currency) {
            case 'EUR':
                return `€${rounded.toFixed(2)}`;
            case 'GBP':
                return `£${rounded.toFixed(2)}`;
            case 'USD':
                return `$${rounded.toFixed(2)}`;
            case 'CHF':
                return `CHF ${rounded.toFixed(2)}`;
            case 'PLN':
                return `${rounded.toFixed(2)} zł`;
            case 'CZK':
                return `${rounded.toFixed(0)} Kč`;
            default:
                return `${currency} ${rounded.toFixed(2)}`;
        }
    }
}

// Initialize when DOM is ready
(function() {
    if (typeof jQuery !== 'undefined') {
        jQuery(document).ready(function() {
            if (jQuery('.shuttle-thank-you').length > 0) {
                window.shuttleThankYouManager = new ShuttleThankYouManager();
                window.shuttleThankYouManager.init();
            }
        });
    } else {
        document.addEventListener('DOMContentLoaded', function() {
            if (document.querySelector('.shuttle-thank-you')) {
                window.shuttleThankYouManager = new ShuttleThankYouManager();
                window.shuttleThankYouManager.init();
            }
        });
    }
})();

